import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";

interface LiquidationSaleFieldsProps {
  isLiquidation: boolean;
  totalInventory: number | null;
  thresholdPercent: number | null;
  endDate: string | null;
  deliveryDays: number | null;
  measurementUnit: string;
  onIsLiquidationChange: (enabled: boolean) => void;
  onTotalInventoryChange: (value: number) => void;
  onThresholdPercentChange: (value: number) => void;
  onEndDateChange: (value: string) => void;
  onDeliveryDaysChange: (value: number) => void;
  // New props for smart suggestion
  stepQuantity?: number;
  packageCoverage?: number | null;
  minQuantity?: number;
  maxQuantity?: number | null;
}

export const LiquidationSaleFields = ({
  isLiquidation,
  totalInventory,
  thresholdPercent,
  deliveryDays,
  measurementUnit,
  onTotalInventoryChange, // Kept for prop compatibility/potential future use, but not used in UI
  onThresholdPercentChange,
  onDeliveryDaysChange,
  stepQuantity = 1,
  packageCoverage = 1,
  minQuantity = 1,
  maxQuantity,
}: Omit<LiquidationSaleFieldsProps, 'endDate' | 'onEndDateChange'> & {
  endDate?: string | null;
  onEndDateChange?: (value: string) => void;
  stepQuantity?: number;
  packageCoverage?: number | null;
  minQuantity?: number;
  maxQuantity?: number | null;
}) => {
  const formatUnit = (unit: string) => {
    const units: Record<string, string> = {
      'square_meter': 'מ"ר',
      'meter': 'מטר',
      'unit': 'יח\'',
      'cubic_meter': 'מ"ק',
      'kilogram': 'ק"ג',
      'liter': 'ליטר',
      'package_by_meter': 'חבילות',
      'package_by_square_meter': 'חבילות'
    };
    return units[unit] || unit;
  };

  // Smart Suggestion Logic
  const getSmartSuggestion = () => {
    if (!totalInventory || !thresholdPercent) return null;

    // Based on user feedback: For package items, totalInventory IS the package count.
    // So we don't need to divide by coverage if it's already packages.
    // logic: if measurementUnit implies packages, treat totalInventory as packages.

    // Check if we are in a package mode
    const isPackageMode = measurementUnit === 'package_by_square_meter' || measurementUnit === 'package_by_meter';

    // Calculate total physical packages
    // If it's package mode, totalInventory is already packages.
    // If it's SQM mode, totalInventory is SQM, so we divide by coverage.
    const totalPackages = isPackageMode
      ? totalInventory
      : Math.floor(totalInventory / (packageCoverage || 1));

    // Calculate current target packages based on user input %
    const currentTargetPackages = Math.floor((totalPackages * thresholdPercent) / 100);

    // Find nearest nice number divisible by stepQuantity
    const step = stepQuantity || 1;

    // Suggest nearest valid step count
    const remainder = currentTargetPackages % step;

    if (remainder === 0) return null; // Already perfect

    // Option A: Round down to nearest step
    const lowerOption = currentTargetPackages - remainder;
    // Option B: Round up to nearest step
    const upperOption = lowerOption + step;

    // Pick the one closer to the original %
    let smartPackages = upperOption <= totalPackages ? upperOption : lowerOption;
    if (smartPackages <= 0) smartPackages = step; // Minimum fallback

    // Calculate what % this package count represents
    const smartPercent = Math.round((smartPackages / totalPackages) * 100);

    return {
      packages: smartPackages,
      percent: smartPercent,
      diff: remainder
    };
  };

  const suggestion = getSmartSuggestion();

  return (
    <>
      {isLiquidation && (
        <>
          <Alert className="mb-4 border-lime bg-lime/10">
            <Info className="h-4 w-4 text-lime" />
            <AlertDescription className="text-sm text-lime-foreground">
              <strong>מכירת חיסול:</strong> במכירה זו, המלאי נמכר במחיר מוזל.
              המכירה נפתחת לזמן מוגבל מרגע הרכישה הראשונה.
              המשלוח יבוצע לאחר הגעה ליעד של {thresholdPercent || 80}% מהמלאי.
              אם היעד לא יושג, הכסף יוחזר ללקוחות.
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="liquidation-threshold">
                אחוז יעד למשלוח (%)
              </Label>
              <Input
                id="liquidation-threshold"
                type="number"
                min="1"
                max="100"
                step="1"
                value={thresholdPercent || ""}
                onChange={(e) => onThresholdPercentChange(parseInt(e.target.value) || 80)}
                placeholder="80"
              />
              {suggestion && (
                <div className="mt-2 text-xs bg-blue-50 text-blue-700 p-2 rounded border border-blue-100 flex flex-col gap-1">
                  <span className="font-semibold">המלצה חכמה:</span>
                  <span>
                    עבור קפיצות של {stepQuantity}, כדאי להגדיר <strong>{suggestion.percent}%</strong>
                    ({suggestion.packages} חבילות).
                  </span>
                  <button
                    type="button"
                    onClick={() => onThresholdPercentChange(suggestion.percent)}
                    className="text-blue-600 underline text-right hover:text-blue-800 font-medium"
                  >
                    לחץ כאן לתיקון אוטומטי
                  </button>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                אחוז המכירות הנדרש מהמלאי (מלאי כולל: {totalInventory || 0})
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="liquidation-duration-days">
                משך המכירה (ימים מרכישה ראשונה)
              </Label>
              <Input
                id="liquidation-duration-days"
                type="number"
                min="1"
                step="1"
                value={deliveryDays || ""}
                onChange={(e) => onDeliveryDaysChange(parseInt(e.target.value) || 7)}
                placeholder="7"
              />
              <p className="text-xs text-muted-foreground">
                מספר הימים שהמכירה תהיה באוויר מרגע הקנייה הראשונה
              </p>
            </div>
          </div>

          {(totalInventory !== null && totalInventory !== undefined && thresholdPercent) && (
            <div className="p-3 bg-muted rounded-md mt-2">
              <p className="text-sm font-medium">סיכום:</p>
              <p className="text-sm text-muted-foreground mt-1">
                • מלאי לחיסול: {totalInventory} {formatUnit(measurementUnit)}
              </p>
              <p className="text-sm text-muted-foreground">
                • יעד נדרש: {Math.ceil((totalInventory * thresholdPercent) / 100)} {formatUnit(measurementUnit)} ({thresholdPercent}%)
              </p>
            </div>
          )}
        </>
      )}
    </>
  );
};
